import { useMemo, useState } from 'react';
import lowerCase from 'lodash/lowerCase';
import { useTranslation } from 'react-i18next';
import { useInventoryData, useMembersData, useUserLocations } from './useCommon';
import { TItemChildren, TListBaseItem } from '../components/base/BList/type';
import { useAppSelector } from './useAppStore';
import { getSelectedFilterItems } from '../store/common/selectors';
import { TLocation, TPersonnel, TVariant } from '../types';

export function useBaseFilter() {
  const { membersData } = useMembersData();
  const { t } = useTranslation('inputs');

  const baseFilters = useMemo(() => {
    const formattedDepartments = membersData.membersDepartments.map(
      (department, index) => ({
        name: department,
        value: `${department?.toLowerCase()}-${index}`,
      }),
    );

    const positions = membersData.membersPositions.map(
      (position, index) => ({
        name: position,
        value: `${position?.toLowerCase()}-${index}`,
      }),
    );

    return [
      {
        value: 'department',
        selectedKey: 'departments',
        name: t('departments'),
        children: formattedDepartments,
      },
      {
        value: 'position',
        selectedKey: 'positions',
        name: t('positions'),
        children: positions,
      },
    ];
  }, [membersData]);

  return {
    baseFilters,
  };
}

export function useBaseInventoryFilter() {
  const { inventoryData } = useInventoryData();
  const { t } = useTranslation('inputs');

  const baseFilters = useMemo(() => {
    const formatedCategories = inventoryData.inventoryCategory.map(
      (category, index) => ({
        name: category,
        value: `${category}-${index}`,
      }),
    );

    const formatedVendors = inventoryData.inventoryVendors.map(
      (vendor, index) => ({
        name: vendor,
        value: `${vendor}-${index}`,
      }),
    );
    const formatedProductTypes = inventoryData.inventoryProductTypes.map(
      (productType, index) => ({
        name: productType,
        value: `${productType}-${index}`,
      }),
    );
    const formatedTags = inventoryData.inventoryTags.map(
      (productType, index) => ({
        name: productType,
        value: `${productType}-${index}`,
      }),
    );

    return [
      {
        value: 'category',
        selectedKey: 'category',
        name: t('Categories'),
        children: formatedCategories,
      },
      {
        value: 'vendor',
        selectedKey: 'vendor',
        name: t('Vendors'),
        children: formatedVendors,
      },
      {
        value: 'productTypes',
        selectedKey: 'productTypes',
        name: t('Product types'),
        children: formatedProductTypes,
      },
      {
        value: 'tags',
        selectedKey: 'tags',
        name: t('Tags'),
        children: formatedTags,
      },
    ];
  }, [inventoryData]);

  return {
    baseFilters,
  };
}

export function useFilter(items = [] as Array<TListBaseItem>, isUseBaseFilters = true, isTasklists = false) {
  const selectedFilterItems = useAppSelector(getSelectedFilterItems);
  const { userLocations } = useUserLocations();
  const { membersData } = useMembersData();
  const { inventoryData } = useInventoryData();
  const { t } = useTranslation(['common', 'inputs']);

  const [selectedItems, setSelectedItems] = useState<{ [key: string]: Array<TItemChildren> }>({
    location: [],
    department: [],
    position: [],
    role: [],
    assign: [],
    creator: [],
    status: [],
    channel: [],
    attributed: [],
    date: [],
    timeoffTypes: [],
    timeoffStatuses: [],
    category: [],
    vendor: [],
    tags: [],
    productTypes: [],
  });

  const baseFilterItems: Array<TListBaseItem> = [
    {
      value: 'role',
      name: t('role'),
      selectedKey: 'role',
      isSelected: false,
      children: [
        { name: t('employee'), value: 'employee' },
        { name: t('manager'), value: 'manager' },
        { name: t('admin'), value: 'admin' },
      ],
    },
  ];

  const filterItems = useMemo(() => {
    const formattedLocations = userLocations.map((location) => ({
      name: location.name,
      value: location.id,
    }));

    if (!isTasklists) {
      items.unshift({
        value: 'location',
        isSelected: false,
        selectedKey: 'locations',
        name: t('locations', { ns: 'inputs' }),
        children: formattedLocations,
      });
    }

    return items;
  }, [membersData, inventoryData, userLocations, items]);

  const filterByPermissions = <
      T extends { permissionLevel?: string } & { personnel?: TPersonnel } & {
        visibility?: Array<{ name: string, id: string }>;
      },
  >(
      items: Array<T>,
      isTasklist = false,
    ) => {
    const permissions = selectedFilterItems.role.map(
      (permission) => permission.value,
    );

    if (isTasklist) {
      return items.filter((item) => item.visibility?.some((permission) => permissions.includes(permission?.id?.toLowerCase())));
    }

    return items.filter((item) => {
      const permission = lowerCase(item.permissionLevel)
        || lowerCase(item.personnel?.permissionLevel);
      return permissions.includes(permission);
    });
  };

  const filterByDepartments = <
    T extends { department?: string } & { personnel?: TPersonnel },
  >(items: Array<T>) => {
    const departments = selectedFilterItems.department.map(
      (department) => lowerCase(department.name),
    );

    return items.filter((item) => {
      const department = lowerCase(item.personnel?.department) || lowerCase(item.department);

      return departments.includes(department);
    });
  };

  const filterByPositions = <
    T extends { position?: string } & { personnel?: TPersonnel },
  >(items: Array<T>) => {
    const positions = selectedFilterItems.position.map(
      (position) => lowerCase(position.name),
    );

    return items.filter((item) => {
      const position = lowerCase(item.personnel?.position) || lowerCase(item.position);

      return positions.includes(position);
    });
  };

  const filterByLocations = <
    T extends { locations?: Array<TLocation> } & { personnel?: TPersonnel },
  >(items: Array<T>) => {
    const localLocations = selectedFilterItems.location.map(
      (item) => item.name?.toLowerCase(),
    );

    return items.filter((item) => {
      const locations = item.locations || item.personnel?.locations;
      const personnelLocations = locations?.map((location) => location.name?.toLowerCase());

      return localLocations.some((location: string) => personnelLocations?.includes(location));
    });
  };

  const filterByCategory = (items: Array<TVariant>) => {
    const localCategories = selectedFilterItems.category.map(
      (item) => item.name?.toLowerCase(),
    );

    return items.filter((item) => localCategories.includes(item.node?.product?.category?.name?.toLowerCase()));
  };

  const filterByVendors = (items: Array<TVariant>) => {
    const localVendors = selectedFilterItems.vendor.map(
      (item) => item.name?.toLowerCase(),
    );

    return items.filter((item) => localVendors.includes(item.node.product.vendor?.toLowerCase()));
  };

  const filterByProductType = (items: Array<TVariant>) => {
    const localProductTypes = selectedFilterItems.productTypes.map(
      (item) => item.name?.toLowerCase(),
    );

    return items.filter((item) => localProductTypes.includes(item.node.product.productType?.toLowerCase()));
  };
  const filterByTags = (items: Array<TVariant>) => {
    const localTags = selectedFilterItems.tags.map(
      (item) => item.name?.toLowerCase(),
    );

    return items.filter((item) => item.node.product.tags.some((tag) => localTags.includes(tag.toLowerCase())));
  };

  const resetFilter = () => {
    setSelectedItems({
      location: [],
      department: [],
      position: [],
      role: [],
      assign: [],
      creator: [],
      status: [],
      channel: [],
      attributed: [],
      date: [],
      timeoffTypes: [],
      timeoffStatuses: [],
      category: [],
      vendor: [],
      tags: [],
      productTypes: [],
    });
  };

  return {
    filterByPermissions,
    filterByPositions,
    filterByDepartments,
    setSelectedItems,
    filterByLocations,
    selectedItems,
    resetFilter,
    filterByCategory,
    filterByVendors,
    filterByProductType,
    filterByTags,
    filterItems: isUseBaseFilters ? [...baseFilterItems, ...filterItems] as Array<TListBaseItem> : filterItems,
  };
}
